import React, {useEffect, useRef, useState} from 'react'
import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Theme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import logo from 'assets/img/logo-pdf.png'
import handSignature from 'assets/img/hand_signature.png'
import {formatPriceNumber, parsePriceNumber} from 'helpers/uiHelper'
import {formatDate} from 'helpers/dateHelper'
import {ISalesSlip} from 'api/productApi'
import CalenderDatePicker from 'components/Calender/CalenderPicker'
import {useTranslation} from 'react-i18next'
import html2canvas from 'html2canvas'
import JsPDF from 'jspdf'
import {usdToCent, centToUSD} from 'helpers/paymentHelper'

interface InvoicePopupProps {
  open: boolean
  handleClose: () => void
  invoice: ISalesSlip | null
  onSubmitInvoice: () => Promise<void>
  isDirty: boolean
  invoiceCurrentDate: Date
  setInvoiceCurrentDate: (date: Date) => void
  onUpdateInvoice: (value: string, key: string) => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      '& > .MuiDialog-container > .MuiPaper-root': {
        [theme.breakpoints.up('xs')]: {
          minWidth: 300,
          borderRadius: 6,
        },
        [theme.breakpoints.up('sm')]: {
          minWidth: 500,
          borderRadius: 6,
        },
        [theme.breakpoints.up('md')]: {
          minWidth: 650,
          borderRadius: 6,
        },
        [theme.breakpoints.up('lg')]: {
          minWidth: 1000,
          borderRadius: 6,
        },
      },
    },
    contentContainer: {
      padding: 20,
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
    },
    invoiceContainer: {
      display: 'flex',
      flexDirection: 'column',
      border: '2px solid black',
      borderRadius: 6,
      background: 'white',
    },
    cellContainer: {
      width: '50%',
      padding: 8,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    },
    cellInput: {
      height: 42,
      padding: 4,
      border: '1px solid #e0e0e0',
      borderRadius: 4,
      width: '100%',
    },
    cellTextarea: {
      height: 80,
      padding: 4,
      border: '1px solid #e0e0e0',
      borderRadius: 4,
      resize: 'none',
      outline: 'none',
    },
    closeButtonWrap: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignContent: 'start',
      '& .MuiTypography-h6': {
        margin: 0,
        lineHeight: 0,
        fontWeight: 700,
      },
    },
    closeButton: {
      backgroundColor: '#f4f4f4',
      width: 20,
      height: 20,
    },
    closeIcon: {
      width: 20,
      height: 20,
      color: '#c5c5c5',
    },
  }),
)

const PDF_PADDING_PX = 8
const SCALE = 3
const PX_TO_MM = 25.4 / 96

const toTextOnlyInvoiceNode = (sourceNode: HTMLDivElement): HTMLDivElement => {
  const clonedNode = sourceNode.cloneNode(true) as HTMLDivElement
  const sourceFormNodes = sourceNode.querySelectorAll('input, textarea')
  const clonedFormNodes = clonedNode.querySelectorAll('input, textarea')
  const sourcePdfTextNodes = sourceNode.querySelectorAll(
    '[data-pdf-text-value]',
  )
  const clonedPdfTextNodes = clonedNode.querySelectorAll(
    '[data-pdf-text-value]',
  )

  clonedFormNodes.forEach((clonedField, index) => {
    const sourceField = sourceFormNodes[index] as
      | HTMLInputElement
      | HTMLTextAreaElement
      | undefined

    const textValue = sourceField?.value?.trim() ?? ''
    const plainTextNode = document.createElement('div')
    plainTextNode.textContent = textValue.length > 0 ? textValue : '-'
    plainTextNode.style.minHeight = `${sourceField?.offsetHeight ?? 20}px`
    plainTextNode.style.padding = '4px'
    plainTextNode.style.display = 'flex'
    plainTextNode.style.alignItems = 'center'
    plainTextNode.style.whiteSpace = 'pre-wrap'
    plainTextNode.style.wordBreak = 'break-word'
    plainTextNode.style.textAlign = sourceField?.style.textAlign ?? 'left'

    clonedField.replaceWith(plainTextNode)
  })

  clonedPdfTextNodes.forEach((clonedNodeItem, index) => {
    const sourceNodeItem = sourcePdfTextNodes[index] as HTMLElement | undefined
    const textValue = sourceNodeItem?.getAttribute('data-pdf-text-value') ?? ''
    const plainTextNode = document.createElement('div')

    plainTextNode.textContent = textValue.trim().length > 0 ? textValue : '-'
    plainTextNode.style.minHeight = `${sourceNodeItem?.offsetHeight ?? 20}px`
    plainTextNode.style.padding = '4px'
    plainTextNode.style.display = 'flex'
    plainTextNode.style.alignItems = 'center'
    plainTextNode.style.whiteSpace = 'pre-wrap'
    plainTextNode.style.wordBreak = 'break-word'
    plainTextNode.style.textAlign = 'left'

    clonedNodeItem.replaceWith(plainTextNode)
  })

  return clonedNode
}

const onDownloadPdf = async (
  input: HTMLDivElement | null,
  fileName = 'invoice.pdf',
) => {
  if (!input) return

  const contentWidthPx = Math.ceil(input.scrollWidth)
  const contentHeightPx = Math.ceil(
    Math.max(input.scrollHeight, input.getBoundingClientRect().height),
  )

  const textOnlyInvoice = toTextOnlyInvoiceNode(input)
  textOnlyInvoice.style.position = 'fixed'
  textOnlyInvoice.style.left = '-10000px'
  textOnlyInvoice.style.top = '0'
  textOnlyInvoice.style.width = `${contentWidthPx}px`
  textOnlyInvoice.style.height = `${contentHeightPx}px`
  textOnlyInvoice.style.overflow = 'hidden'
  textOnlyInvoice.style.zIndex = '-1'
  document.body.appendChild(textOnlyInvoice)

  try {
    const canvas = await html2canvas(textOnlyInvoice, {
      scale: SCALE,
      width: contentWidthPx,
      height: contentHeightPx,
    })
    const imgData = canvas.toDataURL('image/jpeg')

    const contentWidthMm = contentWidthPx * PX_TO_MM
    const contentHeightMm = contentHeightPx * PX_TO_MM
    const paddingMm = PDF_PADDING_PX * PX_TO_MM
    const pageWidthMm = contentWidthMm + paddingMm * 2
    const pageHeightMm = contentHeightMm + paddingMm * 2

    const pdf = new JsPDF(
      pageWidthMm >= pageHeightMm ? 'landscape' : 'portrait',
      'mm',
      [pageWidthMm, pageHeightMm],
    )

    pdf.addImage(
      imgData,
      'JPEG',
      paddingMm,
      paddingMm,
      contentWidthMm,
      contentHeightMm,
    )

    const pdfBlob = pdf.output('blob')
    const file = new File([pdfBlob], fileName, {
      type: 'application/pdf',
    })

    // Download the generated PDF
    const downloadLink = document.createElement('a')
    const objectUrl = URL.createObjectURL(file)
    downloadLink.href = objectUrl
    downloadLink.download = fileName
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
    URL.revokeObjectURL(objectUrl)
  } finally {
    document.body.removeChild(textOnlyInvoice)
  }
}

const formatPriceForDisplay = (
  price: number | string | undefined,
  currency: string,
) => {
  if (price === undefined) return ''

  const priceNumber = typeof price === 'number' ? price : parseFloat(price)
  if (Number.isNaN(priceNumber)) return ''

  switch (currency) {
    case 'KRW':
      return formatPriceNumber(priceNumber)
    case 'USD':
      return formatPriceNumber(usdToCent(priceNumber))
    default:
      return String(price)
  }
}

const InvoicePopup = ({
  open,
  handleClose,
  invoice,
  onSubmitInvoice,
  isDirty,
  invoiceCurrentDate,
  setInvoiceCurrentDate,
  onUpdateInvoice,
}: InvoicePopupProps) => {
  const classes = useStyles()
  const {t} = useTranslation()
  const componentRef = useRef<HTMLDivElement>(null)
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false)
  const [displayPrice, setDisplayPrice] = useState<string>(
    formatPriceForDisplay(
      invoice?.product.price,
      invoice?.product.currency ?? '',
    ),
  )
  const [displayUnitPrice, setDisplayUnitPrice] = useState<string>(
    formatPriceForDisplay(
      invoice?.product.unitPrice,
      invoice?.product.currency ?? '',
    ),
  )

  const handlePriceChange = (value: string, key: string) => {
    let data = parsePriceNumber(value) // for validation, if NaN, return 0 and display empty string
    if (invoice?.product.currency === 'USD' && !Number.isNaN(data))
      data = centToUSD(data)
    console.log('handlePriceChange', {value, data, key})
    switch (key) {
      case 'price':
        setDisplayPrice(
          formatPriceForDisplay(data, invoice?.product.currency ?? ''),
        )
        break
      case 'unitPrice':
        setDisplayUnitPrice(
          formatPriceForDisplay(data, invoice?.product.currency ?? ''),
        )
        break
      default:
        break
    }

    onUpdateInvoice(String(data), key)
  }

  const getInvoicePdfFileName = () => {
    const slipNo = invoice?.salesSlipNo?.trim() || 'no-number'
    const year = invoiceCurrentDate.getFullYear()
    const month = String(invoiceCurrentDate.getMonth() + 1).padStart(2, '0')
    const day = String(invoiceCurrentDate.getDate()).padStart(2, '0')
    const invoiceDate = `${year}${month}${day}`

    const safeSlipNo = slipNo.replace(/[^a-zA-Z0-9-_]/g, '_')
    const safeDate = invoiceDate.replace(/[^a-zA-Z0-9-_]/g, '_')

    return `invoice_${safeSlipNo}_${safeDate}.pdf`
  }

  const handlePdfClick = () => {
    if (isDirty) {
      setSaveConfirmOpen(true)
    } else {
      onDownloadPdf(componentRef.current, getInvoicePdfFileName())
    }
  }

  const handleSaveAndDownload = async () => {
    setSaveConfirmOpen(false)
    try {
      await onSubmitInvoice()
      await onDownloadPdf(componentRef.current, getInvoicePdfFileName())
    } catch {
      // 오류는 onSubmitInvoice 내부에서 이미 failure modal로 처리됨
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} className={classes.container}>
      <div className={classes.contentContainer}>
        <div className={classes.closeButtonWrap}>
          <IconButton
            color='secondary'
            aria-label='favorite'
            className={classes.closeButton}
            onClick={handleClose}
          >
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </div>
        <div className={classes.invoiceContainer} ref={componentRef}>
          <div
            style={{
              padding: 12,
              textAlign: 'center',
              borderBottom: '2px solid black',
            }}
          >
            <span
              style={{
                fontWeight: 700,
                color: 'black',
                fontSize: 22,
              }}
            >
              INVOICE
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              borderBottom: '2px solid black',
            }}
          >
            <div
              className={classes.cellContainer}
              style={{
                borderRight: '2px solid black',
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  color: 'black',
                  fontSize: 16,
                }}
              >
                Buyer/Payer
              </div>
              <input
                className={classes.cellInput}
                style={{
                  width: '60%',
                }}
                value={invoice?.buyer.orgName}
                onChange={(e) => onUpdateInvoice(e.target.value, 'orgName')}
              />
              <input
                className={classes.cellInput}
                value={invoice?.buyer.address}
                onChange={(e) => onUpdateInvoice(e.target.value, 'address')}
              />
              <input
                className={classes.cellInput}
                value={invoice?.buyer.email}
                onChange={(e) => onUpdateInvoice(e.target.value, 'email')}
              />
            </div>
            <div className={classes.cellContainer}>
              <div
                style={{
                  fontWeight: 700,
                  color: 'black',
                  fontSize: 16,
                }}
              >
                No. & Date of Invoice
              </div>
              <input
                className={classes.cellInput}
                style={{
                  width: '60%',
                }}
                value={invoice?.salesSlipNo}
                onChange={(e) => onUpdateInvoice(e.target.value, 'salesSlipNo')}
              />
              <div
                style={{
                  width: '60%',
                }}
                data-pdf-text-value={formatDate(invoiceCurrentDate)}
              >
                <CalenderDatePicker
                  onChangeValue={(val) =>
                    setInvoiceCurrentDate(val ?? new Date())
                  }
                  dateValue={invoiceCurrentDate}
                  enablePassDate
                />
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              borderBottom: '2px solid black',
            }}
          >
            <div
              className={classes.cellContainer}
              style={{
                borderRight: '2px solid black',
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  color: 'black',
                  fontSize: 16,
                }}
              >
                Seller / Payee
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 5,
                }}
              >
                <span>IMEDISYNC INC Attn:</span>
                <input
                  className={classes.cellInput}
                  value={invoice?.seller.name}
                  onChange={(e) =>
                    onUpdateInvoice(e.target.value, 'sellerName')
                  }
                />
              </div>
              5FL, 520, Teheran-ro, Gangnam-gu, Seoul, Republic of Korea (06181)
            </div>
            <div className={classes.cellContainer}>
              <div
                style={{
                  fontWeight: 700,
                  color: 'black',
                  fontSize: 16,
                }}
              >
                Country of Origin
              </div>
              Republic of Korea
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              borderBottom: '2px solid black',
            }}
          >
            <div
              className={classes.cellContainer}
              style={{
                borderRight: '2px solid black',
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  color: 'black',
                  fontSize: 16,
                }}
              >
                Notify Party
              </div>
            </div>
            <div className={classes.cellContainer}>
              <div
                style={{
                  fontWeight: 700,
                  color: 'black',
                  fontSize: 16,
                }}
              >
                Other reference
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              borderBottom: '2px solid black',
            }}
          >
            <div
              className={classes.cellContainer}
              style={{
                borderRight: '2px solid black',
                padding: 0,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '50%',
                    padding: 8,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: 'black',
                      fontSize: 16,
                    }}
                  >
                    Port of loading
                  </div>
                  NA
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '50%',
                    borderLeft: '2px solid black',
                    padding: 8,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: 'black',
                      fontSize: 16,
                    }}
                  >
                    Port of unloading / Destination
                  </div>
                  NA
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '50%',
                    borderTop: '2px solid black',
                    padding: 8,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: 'black',
                      fontSize: 16,
                    }}
                  >
                    Vessel / Flight
                  </div>
                  NA
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '50%',
                    borderLeft: '2px solid black',
                    borderTop: '2px solid black',
                    padding: 8,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: 'black',
                      fontSize: 16,
                    }}
                  >
                    Sailing on or about
                  </div>
                  NA
                </div>
              </div>
            </div>
            <div className={classes.cellContainer}>
              <div
                style={{
                  fontWeight: 700,
                  color: 'black',
                  fontSize: 16,
                }}
              >
                Remark
              </div>
              <textarea
                className={classes.cellTextarea}
                value={invoice?.remark}
                onChange={(e) => onUpdateInvoice(e.target.value, 'remark')}
              />
            </div>
          </div>
          <div
            className={classes.cellContainer}
            style={{
              width: '100%',
              borderBottom: '2px solid black',
            }}
          >
            <div
              style={{
                fontWeight: 700,
                color: 'black',
                fontSize: 16,
              }}
            >
              AWB NO.
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              alignItems: 'center',
              width: '100%',
              gridTemplateColumns: '1fr 3fr 1fr 1fr 1fr',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: 8,
                height: '100%',
                borderRight: '2px solid black',
              }}
            >
              HS Code
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: 8,
                height: '100%',
                borderRight: '2px solid black',
              }}
            >
              Product Name
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: 8,
                height: '100%',
                borderRight: '2px solid black',
              }}
            >
              Quantity
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: 8,
                height: '100%',
                borderRight: '2px solid black',
              }}
            >
              Unit-Price ({invoice?.product.currency})
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                height: '100%',
                padding: 8,
              }}
            >
              Amount ({invoice?.product.currency})
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 3fr 1fr 1fr 1fr',
              width: '100%',
              borderTop: '2px solid black',
            }}
          >
            <div
              style={{
                textAlign: 'center',
                padding: 8,
                borderRight: '2px solid black',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              NA
            </div>
            <div
              style={{
                textAlign: 'center',
                padding: 8,
                borderRight: '2px solid black',
              }}
            >
              <input
                className={classes.cellInput}
                value={invoice?.product.name}
                onChange={(e) => onUpdateInvoice(e.target.value, 'name')}
              />
            </div>
            <div
              style={{
                textAlign: 'center',
                padding: 8,
                borderRight: '2px solid black',
              }}
            >
              <input
                className={classes.cellInput}
                value={formatPriceNumber(invoice?.product.quantity ?? 0)}
                style={{
                  textAlign: 'right',
                }}
                onChange={(e) => onUpdateInvoice(e.target.value, 'quantity')}
              />
            </div>
            <div
              style={{
                textAlign: 'center',
                padding: 8,
                borderRight: '2px solid black',
              }}
            >
              <input
                className={classes.cellInput}
                name='unitPrice'
                value={displayUnitPrice}
                style={{
                  textAlign: 'right',
                }}
                onChange={(e) =>
                  handlePriceChange(e.target.value, e.target.name)
                }
              />
            </div>
            <div
              style={{
                textAlign: 'center',
                padding: 8,
              }}
            >
              <input
                className={classes.cellInput}
                name='price'
                value={displayPrice}
                style={{
                  textAlign: 'right',
                }}
                onChange={(e) =>
                  handlePriceChange(e.target.value, e.target.name)
                }
              />
            </div>
          </div>
          <div
            className={classes.cellContainer}
            style={{
              width: '100%',
              borderTop: '2px solid black',
            }}
          >
            <img src={logo} alt='logo' style={{width: 150, height: 'auto'}} />
            ** Beneficiary Bank
            <br />
            ** Bank name: KOOKMIN Bank
            <br />
            ** SWIFT Code: CZNBKRSE
            <br />
            **Account No.: 342368-11-006614
            <br />
            Bank Address: 26, 8, Gukjegeumyung-ro gil, Yeongdeungpo-gu, Seoul,
            Republic of Korea
          </div>
          <div
            style={{
              display: 'flex',
              width: '100%',
              borderTop: '2px solid black',
            }}
          >
            <div
              style={{
                padding: 8,
                width: '55%',
                borderRight: '2px solid black',
              }}
            >
              MADE IN KOREA
            </div>
            <div
              style={{
                padding: 8,
                width: '30%',
                borderRight: '2px solid black',
              }}
            >
              Signed by
            </div>
            <div
              style={{
                padding: 8,
                width: '25%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={handSignature}
                alt='hand_signature'
                style={{width: 150, height: 'auto'}}
              />
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              width: 64,
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 20,
              justifySelf: 'center',
            }}
          >
            <Button onClick={handleClose} variant='contained' color='primary'>
              {t('ICancel')}
            </Button>
            <Button
              onClick={onSubmitInvoice}
              variant='contained'
              color='primary'
            >
              {t('IConfirm')}
            </Button>
          </div>
          <Button
            onClick={handlePdfClick}
            variant='contained'
            color='primary'
            style={{
              justifySelf: 'flex-end',
            }}
          >
            PDF
          </Button>
        </div>
      </div>
      <Dialog open={saveConfirmOpen} onClose={() => setSaveConfirmOpen(false)}>
        <DialogTitle>{t('IInvoiceSaveConfirmTitle')}</DialogTitle>
        <DialogContent>{t('IInvoiceSaveConfirmMessage')}</DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveConfirmOpen(false)} color='default'>
            {t('INo')}
          </Button>
          <Button onClick={handleSaveAndDownload} color='primary'>
            {t('IYes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default InvoicePopup
